import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Modal } from '@common/components/ui';
import { Button, Input } from '@common/components/ui';
import { CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface BarcodeScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBarcodeScanned: (barcode: string) => Promise<void>;
}

export const BarcodeScannerModal = ({
  isOpen,
  onClose,
  onBarcodeScanned,
}: BarcodeScannerModalProps) => {
  const [manualBarcode, setManualBarcode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [cameras, setCameras] = useState<{ id: string; label: string }[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const hasScannedRef = useRef(false);

  // Inject CSS to control html5-qrcode internal elements
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'barcode-scanner-styles';
    style.textContent = `
      #barcode-reader video,
      #barcode-reader canvas {
        position: relative !important;
        z-index: 1 !important;
        max-height: 400px !important;
        pointer-events: none !important;
      }
      #barcode-reader {
        position: relative !important;
        z-index: 1 !important;
      }
      #barcode-reader > div {
        position: relative !important;
        z-index: 1 !important;
        pointer-events: none !important;
      }
      /* Ensure buttons are clickable */
      button {
        pointer-events: auto !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById('barcode-scanner-styles');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  useEffect(() => {
    // Get available cameras when modal opens
    if (isOpen) {
      // Reset state when modal opens
      hasScannedRef.current = false;
      setIsProcessing(false);
      
      Html5Qrcode.getCameras()
        .then((devices) => {
          if (devices && devices.length) {
            const cameraList = devices.map(d => ({ id: d.id, label: d.label }));
            setCameras(cameraList);
            // Select the first camera by default (usually back camera on mobile)
            setSelectedCameraId(cameraList[0].id);
          } else {
            toast.error('No cameras found on this device');
          }
        })
        .catch((err) => {
          console.error('Error getting cameras:', err);
          toast.error('Unable to access camera');
        });
    }

    return () => {
      // Cleanup scanner when component unmounts
      // Don't null the ref here - let the modal close handler do it
    };
  }, [isOpen]);

  // Additional cleanup effect to ensure camera stops when modal closes
  useEffect(() => {
    if (!isOpen && scannerRef.current) {
      const scanner = scannerRef.current;
      try {
        scanner
          .stop()
          .then(() => {
            try {
              scanner.clear();
            } catch (e) {
              // Silently fail
            }
          })
          .catch(() => {
            try {
              scanner.clear();
            } catch (e) {
              // Silently fail
            }
          })
          .finally(() => {
            scannerRef.current = null;
            setIsScanning(false);
          });
      } catch (e) {
        // Scanner not running
        try {
          scanner.clear();
        } catch (clearErr) {
          // Silently fail
        }
        scannerRef.current = null;
        setIsScanning(false);
      }
    }
  }, [isOpen]);

  const startScanning = async () => {
    if (cameras.length === 0) {
      toast.error('No cameras available');
      return;
    }

    if (!selectedCameraId) {
      toast.error('Please select a camera');
      return;
    }

    try {
      setIsScanning(true);
      
      // Use the selected camera
      const cameraId = selectedCameraId;
      
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode('barcode-reader');
      }

      await scannerRef.current.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        async (decodedText) => {
          // Successfully scanned - prevent multiple scans
          if (hasScannedRef.current || isProcessing) {
            return;
          }
          
          hasScannedRef.current = true;
          setIsProcessing(true);
          
          // Stop scanner immediately
          stopScanning();
          
          try {
            // Call the callback and wait for it to complete
            await onBarcodeScanned(decodedText);
            toast.success('Barcode scanned successfully!');
            
            // Close modal after success
            setTimeout(() => {
              onClose();
            }, 300);
          } catch (error) {
            console.error('Error processing barcode:', error);
            setIsProcessing(false);
            hasScannedRef.current = false;
          }
        },
        () => {
          // Scanning error (normal when no barcode is detected)
          // Don't show error for each frame
        }
      );
      
    } catch (err: any) {
      console.error('Error starting scanner:', err);
      toast.error('Failed to start camera: ' + err.message);
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (scannerRef.current) {
      const scanner = scannerRef.current;
      try {
        scanner
          .stop()
          .then(() => {
            try {
              scanner.clear();
            } catch (e) {
              // Silently fail
            }
            setIsScanning(false);
          })
          .catch((err) => {
            console.error('Error stopping scanner:', err);
            setIsScanning(false);
            // Try to clear even if stop failed
            try {
              scanner.clear();
            } catch (e) {
              // Silently fail
            }
          });
      } catch (e) {
        setIsScanning(false);
      }
    } else {
      setIsScanning(false);
    }
  };

  const handleManualSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    
    if (isProcessing) {
      return; // Prevent double submission
    }
    
    if (manualBarcode.trim()) {
      setIsProcessing(true);
      
      try {
        // Call the callback and wait for backend request to complete
        await onBarcodeScanned(manualBarcode.trim());
        toast.success('Barcode processed successfully!');
        
        // Close modal after successful processing
        setTimeout(() => {
          setManualBarcode('');
          setIsProcessing(false);
          onClose();
        }, 300);
      } catch (error) {
        console.error('Error processing barcode:', error);
        setIsProcessing(false);
        // Keep modal open so user can try again or fix the barcode
      }
    } else {
      toast.error('Please enter a barcode');
    }
  };

  const handleClose = () => {
    if (isProcessing) {
      return; // Don't allow closing while processing
    }
    
    // Stop scanning before closing
    if (scannerRef.current) {
      const scanner = scannerRef.current;
      try {
        scanner
          .stop()
          .then(() => {
            try {
              scanner.clear();
            } catch (e) {
              // Silently fail
            }
            scannerRef.current = null;
            setIsScanning(false);
            setManualBarcode('');
            onClose();
          })
          .catch((err) => {
            console.error('Error stopping scanner on close:', err);
            // Continue closing even if stop fails
            try {
              scanner.clear();
            } catch (e) {
              // Silently fail
            }
            scannerRef.current = null;
            setIsScanning(false);
            setManualBarcode('');
            onClose();
          });
      } catch (e) {
        // Scanner not running, just close
        try {
          scanner.clear();
        } catch (clearErr) {
          // Silently fail
        }
        scannerRef.current = null;
        setIsScanning(false);
        setManualBarcode('');
        onClose();
      }
    } else {
      setManualBarcode('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Scan Product Barcode"
      size="lg"
      closeOnOutsideClick={false}
    >
      <div className="p-6 space-y-6">
        {/* Camera Scanner Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between relative z-30">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              Scan with Camera
            </h4>
            {cameras.length > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {cameras.length} camera{cameras.length !== 1 ? 's' : ''} available
              </span>
            )}
          </div>

          {/* Camera Controls - Moved to top for better UX */}
          <div className="flex gap-3 relative z-50 bg-white dark:bg-gray-800" style={{ pointerEvents: 'auto' }}>
            {!isScanning ? (
              <Button
                type="button"
                onClick={startScanning}
                disabled={cameras.length === 0 || isProcessing}
                className="flex-1 relative z-50"
                icon={<CameraIcon className="h-5 w-5" />}
                style={{ pointerEvents: 'auto' }}
              >
                Start Camera
              </Button>
            ) : (
              <Button
                type="button"
                onClick={stopScanning}
                variant="outline"
                className="flex-1 relative z-50"
                disabled={isProcessing}
                icon={<XMarkIcon className="h-5 w-5" />}
                style={{ pointerEvents: 'auto' }}
              >
                Stop Camera
              </Button>
            )}
          </div>

          {/* Camera Selector */}
          {cameras.length > 1 && (
            <div className="relative z-50 bg-white dark:bg-gray-800" style={{ pointerEvents: 'auto' }}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Camera
              </label>
              <select
                value={selectedCameraId}
                onChange={(e) => {
                  setSelectedCameraId(e.target.value);
                  // If currently scanning, stop and restart with new camera
                  if (isScanning) {
                    stopScanning();
                  }
                }}
                disabled={isScanning || isProcessing}
                className="relative z-50 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                style={{ pointerEvents: 'auto' }}
              >
                {cameras.map((camera) => (
                  <option key={camera.id} value={camera.id}>
                    {camera.label || `Camera ${camera.id}`}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isScanning ? 'Stop camera to switch' : 'Select a camera before starting'}
              </p>
            </div>
          )}

          {/* Camera Preview */}
          <div className="relative z-10 isolate">
            <div
              id="barcode-reader"
              className="w-full rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-900"
              style={{ minHeight: isScanning ? '300px' : '0', maxHeight: '400px' }}
            />
            
            {!isScanning && (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <CameraIcon className="h-16 w-16 text-gray-400" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Click the button above to start scanning
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200 dark:border-gray-700" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              Or enter manually
            </span>
          </div>
        </div>

        {/* Manual Entry Section */}
        <div className="space-y-4">
          <div>
            <Input
              label="Enter Barcode Manually"
              value={manualBarcode}
              onChange={(e) => setManualBarcode(e.target.value)}
              placeholder="Enter barcode number"
              type="text"
              disabled={isProcessing}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleManualSubmit(e as any);
                }
              }}
            />
          </div>

          {isProcessing && (
            <div className="text-center text-sm text-indigo-600 dark:text-indigo-400">
              Processing barcode...
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button 
              type="button"
              onClick={handleManualSubmit}
              className="flex-1"
              disabled={isProcessing}
              loading={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Submit Barcode'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
